import type { ReactElement } from 'react';
import { AddIcon } from 'tdesign-icons-react';
import type { FormRule } from 'tdesign-react';
import { Form, Input, RadioGroup, Textarea } from 'tdesign-react';
import {
  ruleEmail,
  ruleLength,
  ruleNoChinese,
  rulePhone,
  ruleRequired,
} from '@ssv-website/hooks';
import { AvatarUploadField } from './AvatarUploadField';
import {
  BIO_EN_PLACEHOLDER,
  BIO_ZH_PLACEHOLDER,
  INTRODUCTION_EN_MAX_LENGTH,
  INTRODUCTION_MAX_LENGTH,
  MAX_TITLE_COUNT,
  MOBILE_FIELD_SECTION_CLASS,
  TITLE_MAX_COUNT_MESSAGE,
  TITLE_ROW_COUNT,
} from './config';
import { createEmptyTitleRow } from './form-helpers';
import { DeleteActionIcon } from './icons';
import { FieldLabel } from './shared';

const { FormItem, FormList } = Form;

const CONTACT_PERMISSION_OPTIONS = [
  {
    label: '允许',
    value: 1,
  },
  {
    label: '拒绝',
    value: 0,
  },
];
const CONTACT_PERMISSION_LABEL =
  '是否允许通过站内信联系您(您可以收取站内通知、好友申请、联系人留言等信息)';
const CONTACT_PERMISSION_REQUIRED_MESSAGE = '请选择是否允许通过站内信联系您';
const CONTACT_PERMISSION_RULES = [
  ruleRequired({
    content: CONTACT_PERMISSION_REQUIRED_MESSAGE,
  }),
];

const createBioRules = (label: string) => [
  ruleRequired({ text: label }),
  ruleNoChinese,
  ruleLength(INTRODUCTION_EN_MAX_LENGTH),
];

const createBioRulesZh = (label: string) => [
  ruleRequired({ text: label }),
  ruleLength(INTRODUCTION_MAX_LENGTH),
];

type SignupTextareaProps = {
  autosize?: {
    minRows?: number;
    maxRows?: number;
  };
  className?: string;
  value?: string;
  maxlength?: number;
  placeholder: string;
  disabled?: boolean;
  onBlur?: (...args: any[]) => void;
  onChange?: (...args: any[]) => void;
  onFocus?: (...args: any[]) => void;
};

function SignupTextarea({
  autosize,
  className,
  value,
  maxlength = INTRODUCTION_MAX_LENGTH,
  placeholder,
  disabled = false,
  onBlur,
  onChange,
  onFocus,
}: SignupTextareaProps) {
  return (
    <Textarea
      autosize={autosize}
      className={className}
      value={value}
      maxlength={maxlength}
      allowInputOverMax
      placeholder={placeholder}
      disabled={disabled}
      onChange={(...args) => {
        onChange?.(...args);
      }}
      onFocus={(...args) => {
        onFocus?.(...args);
      }}
      onBlur={(...args) => {
        onBlur?.(...args);
      }}
    />
  );
}

const createTitleRequiredRule = ({
  isRequired,
  field,
  label,
}: {
  form: any;
  index: number;
  isRequired: boolean;
  field: 'titleCn' | 'titleEn';
  label: string;
}) => ({
  // 第一行头衔强制中英都填；后续行填写时中英任一项即可。
  validator: (value: unknown) => {
    if (isRequired) {
      return !!String(value ?? '').trim();
    }

    return true;
  },
  message: `请输入${label}${field === 'titleCn' ? '中文' : '英文'}头衔`,
});

export function DesktopAvatarField({
  onUploadStateChange,
  disabled = false,
}: {
  onUploadStateChange?: () => void;
  disabled?: boolean;
}) {
  return (
    <div className="pb-6">
      <AvatarUploadField
        label={<FieldLabel text="头像" required />}
        name="avatar"
        requiredRuleContent="请上传头像"
        uploadClassName="signup-2026-desktop-upload"
        uploadWrapperClassName="w-[120px]"
        promptClassName="mt-2 text-xs leading-5 text-[rgba(0,0,0,0.4)]"
        prompt="请上传您的高清真实头像，大小不超过2M"
        onUploadStateChange={onUploadStateChange}
        disabled={disabled}
      />
    </div>
  );
}

export function MobileAvatarField({
  onUploadStateChange,
  disabled = false,
}: {
  onUploadStateChange?: () => void;
  disabled?: boolean;
}) {
  return (
    <AvatarUploadField
      label={<FieldLabel text="你的头像" required />}
      name="avatar"
      requiredRuleContent="请上传头像"
      promptClassName="mt-1 text-xs leading-5 text-[rgba(0,0,0,0.4)]"
      prompt="请上传您的高清真实头像，大小不超过2M"
      uploadClassName="signup-2026-mobile-upload"
      uploadWrapperClassName="w-[105px]"
      onUploadStateChange={onUploadStateChange}
      disabled={disabled}
      useCustomMobileUploadUi
    />
  );
}

export function DesktopInputField({
  label,
  name,
  placeholder,
  required = false,
  rules,
  maxlength,
  disabled = false,
}: {
  label: string;
  name: string;
  placeholder: string;
  required?: boolean;
  rules?: FormRule[];
  maxlength?: number;
  disabled?: boolean;
}) {
  return (
    <FormItem
      label={<FieldLabel text={label} required={required} />}
      name={name}
      rules={rules}
    >
      <Input
        size="medium"
        className="!h-[34px]"
        maxlength={maxlength}
        placeholder={placeholder}
        disabled={disabled}
      />
    </FormItem>
  );
}

export function DesktopBioFields({ disabled = false }: { disabled?: boolean }) {
  return (
    <div className="pb-6">
      <div className="pb-2">
        <FieldLabel text="个人简介" required />
      </div>
      <div className="flex flex-col gap-2">
        <FormItem
          className="signup-2026-desktop-bio-zh"
          name="bioCn"
          rules={createBioRulesZh('个人简介')}
        >
          <SignupTextarea
            className="!min-h-[54px]"
            placeholder={BIO_ZH_PLACEHOLDER}
            disabled={disabled}
          />
        </FormItem>
        <div className="">
          <FieldLabel text="个人简介（英文）" required />
        </div>
        <FormItem
          className="signup-2026-desktop-bio-en"
          name="bioEn"
          rules={createBioRules('个人简介（英文）')}
        >
          <SignupTextarea
            className="!min-h-[74px]"
            placeholder={BIO_EN_PLACEHOLDER}
            maxlength={600}
            disabled={disabled}
          />
        </FormItem>
      </div>
    </div>
  );
}

export function MobileInputField({
  label,
  name,
  placeholder,
  required = false,
  rules,
  maxlength,
  labelAction,
  suffixIcon,
  disabled = false,
  readonly = false,
}: {
  label: string;
  name: string | (string | number)[];
  placeholder: string;
  required?: boolean;
  rules?: FormRule[];
  maxlength?: number;
  labelAction?: ReactElement;
  suffixIcon?: ReactElement;
  disabled?: boolean;
  readonly?: boolean;
}) {
  return (
    <div className={MOBILE_FIELD_SECTION_CLASS}>
      <div className="signup-2026-mobile-field-label-wrapper flex items-center justify-between gap-3">
        <FieldLabel text={label} required={required} />
        {labelAction ?? null}
      </div>
      <FormItem name={name} rules={rules}>
        <Input
          size="large"
          maxlength={maxlength}
          allowInputOverMax
          placeholder={placeholder}
          suffixIcon={suffixIcon}
          disabled={disabled}
          readonly={readonly}
        />
      </FormItem>
    </div>
  );
}

export function MobileTextareaField({
  label,
  name,
  placeholder,
  required = false,
  rules,
  maxlength,
  disabled = false,
}: {
  label: string;
  name: string;
  placeholder: string;
  required?: boolean;
  rules?: FormRule[];
  maxlength?: number;
  disabled?: boolean;
}) {
  return (
    <>
      <div className="signup-2026-mobile-field-label-wrapper signup-2026-mobile-textarea-label">
        <FieldLabel text={label} required={required} />
      </div>
      <FormItem name={name} rules={rules}>
        <SignupTextarea
          className="signup-2026-mobile-textarea-field"
          placeholder={placeholder}
          maxlength={maxlength}
          disabled={disabled}
        />
      </FormItem>
    </>
  );
}

export function TitleListFields({
  form,
  mobile = false,
  disabled = false,
}: {
  form: any;
  mobile?: boolean;
  disabled?: boolean;
}) {
  return (
    <FormList name="titles">
      {(fields, { add, remove }) => (
        <>
          {fields.map(({ key, name }) => {
            const index = Number(name);
            const isRequired = index === 0;
            const label = `头衔${index + 1}`;

            if (mobile) {
              return (
                <div key={key}>
                  <MobileInputField
                    label={`中文${label}`}
                    name={[name, 'titleCn']}
                    placeholder="请输入中文头衔"
                    required={isRequired}
                    rules={[
                      createTitleRequiredRule({
                        form,
                        index,
                        isRequired,
                        field: 'titleCn',
                        label,
                      }),
                      ruleLength(50),
                    ]}
                    maxlength={50}
                    labelAction={
                      !disabled && index >= TITLE_ROW_COUNT ? (
                        <button
                          type="button"
                          aria-label="删除头衔"
                          className="inline-flex h-4 w-4 shrink-0 items-center justify-center text-[rgba(0,0,0,0.4)]"
                          onClick={() => remove(name)}
                        >
                          <span className="scale-[0.8]">
                            <DeleteActionIcon />
                          </span>
                        </button>
                      ) : undefined
                    }
                    disabled={disabled}
                  />
                  <div className={MOBILE_FIELD_SECTION_CLASS}>
                    <div className="signup-2026-mobile-field-label-wrapper">
                      <FieldLabel text={`英文${label}`} required={isRequired} />
                    </div>
                    <FormItem
                      name={[name, 'titleEn']}
                      rules={[
                        createTitleRequiredRule({
                          form,
                          index,
                          isRequired,
                          field: 'titleEn',
                          label,
                        }),
                        ruleNoChinese,
                        ruleLength(100),
                      ]}
                    >
                      <Input
                        size="large"
                        maxlength={100}
                        disabled={disabled}
                        placeholder="请输入英文头衔"
                      />
                    </FormItem>
                  </div>
                </div>
              );
            }

            return (
              <div key={key} className="pb-6">
                <div className="mb-2 flex items-center justify-between">
                  <FieldLabel text={label} required={isRequired} />
                  {!disabled && index >= TITLE_ROW_COUNT ? (
                    <button
                      type="button"
                      aria-label="删除头衔"
                      className="inline-flex h-5 w-5 items-center justify-center"
                      onClick={() => remove(name)}
                    >
                      <DeleteActionIcon />
                    </button>
                  ) : null}
                </div>
                <div className="flex gap-8">
                  <div className="w-[400px]">
                    <FormItem
                      name={[name, 'titleCn']}
                      rules={[
                        createTitleRequiredRule({
                          form,
                          index,
                          isRequired,
                          field: 'titleCn',
                          label,
                        }),
                        ruleLength(50),
                      ]}
                    >
                      <Input
                        size="medium"
                        className="!h-[34px]"
                        maxlength={50}
                        disabled={disabled}
                        placeholder="请输入中文头衔"
                      />
                    </FormItem>
                  </div>
                  <div className="w-[400px]">
                    <FormItem
                      name={[name, 'titleEn']}
                      rules={[
                        createTitleRequiredRule({
                          form,
                          index,
                          isRequired,
                          field: 'titleEn',
                          label,
                        }),
                        ruleNoChinese,
                        ruleLength(100),
                      ]}
                    >
                      <Input
                        size="medium"
                        className="!h-[34px]"
                        maxlength={100}
                        disabled={disabled}
                        placeholder="请输入英文头衔"
                      />
                    </FormItem>
                  </div>
                </div>
              </div>
            );
          })}

          {!disabled &&
            (mobile ? (
              <div className={MOBILE_FIELD_SECTION_CLASS}>
                <button
                  type="button"
                  disabled={fields.length >= MAX_TITLE_COUNT}
                  className={`inline-flex h-6 items-center gap-1 rounded-full border border-[#DCDCDC] bg-white px-[10px] text-xs leading-4 ${
                    fields.length >= MAX_TITLE_COUNT
                      ? 'cursor-not-allowed text-[rgba(0,0,0,0.4)]'
                      : 'text-[rgba(0,0,0,0.9)]'
                  }`}
                  onClick={() => add(createEmptyTitleRow())}
                  title={
                    fields.length >= MAX_TITLE_COUNT
                      ? TITLE_MAX_COUNT_MESSAGE
                      : undefined
                  }
                >
                  <AddIcon size={12} color="currentColor" />
                  添加头衔
                </button>
                {fields.length >= MAX_TITLE_COUNT && (
                  <span className="ml-2 text-xs text-[rgba(0,0,0,0.4)]">
                    {TITLE_MAX_COUNT_MESSAGE}
                  </span>
                )}
              </div>
            ) : (
              <div className="pb-6">
                <button
                  type="button"
                  disabled={fields.length >= MAX_TITLE_COUNT}
                  className={`inline-flex items-center justify-center gap-2 rounded-[3px] border border-[#DCDCDC] bg-white px-4 py-[5px] text-sm leading-[22px] ${
                    fields.length >= MAX_TITLE_COUNT
                      ? 'cursor-not-allowed text-[rgba(0,0,0,0.4)]'
                      : 'text-[rgba(0,0,0,0.9)]'
                  }`}
                  onClick={() => add(createEmptyTitleRow())}
                  title={
                    fields.length >= MAX_TITLE_COUNT
                      ? TITLE_MAX_COUNT_MESSAGE
                      : undefined
                  }
                >
                  <AddIcon size={16} color="currentColor" />
                  新增头衔
                </button>
                {fields.length >= MAX_TITLE_COUNT && (
                  <span className="ml-3 text-sm text-[rgba(0,0,0,0.4)]">
                    {TITLE_MAX_COUNT_MESSAGE}
                  </span>
                )}
              </div>
            ))}
        </>
      )}
    </FormList>
  );
}

export function ContactPermissionField({
  desktop = false,
  disabled = false,
}: {
  desktop?: boolean;
  disabled?: boolean;
}) {
  if (!desktop) {
    return (
      <div className={MOBILE_FIELD_SECTION_CLASS}>
        <div className="signup-2026-mobile-field-label-wrapper">
          <FieldLabel text={CONTACT_PERMISSION_LABEL} required />
        </div>
        <FormItem name="allowContact" rules={CONTACT_PERMISSION_RULES}>
          <RadioGroup
            options={CONTACT_PERMISSION_OPTIONS}
            className="flex items-center"
            disabled={disabled}
          />
        </FormItem>
      </div>
    );
  }

  return (
    <FormItem
      label={<FieldLabel text={CONTACT_PERMISSION_LABEL} required />}
      name="allowContact"
      rules={CONTACT_PERMISSION_RULES}
    >
      <RadioGroup
        options={CONTACT_PERMISSION_OPTIONS}
        className={desktop ? undefined : 'flex items-center'}
        disabled={disabled}
      />
    </FormItem>
  );
}

export const phoneRules = [
  ruleRequired({ text: '电话' }),
  rulePhone,
  ruleLength(20),
];

export const emailRules = [
  ruleRequired({ text: '邮箱' }),
  ruleEmail,
  ruleLength(100),
];
